/**
 * NovalaDatePicker.
 *
 * The shared date picker Claire wants used across the whole Novala
 * app (not the native browser date input). Chip header pattern:
 *
 *   ‹  [Month ▾]  [Year ▾]  ›
 *
 * Today's cell = #E7EAF0 background.
 * Selected cell = #263238 background, white text.
 *
 * Mounts as a React Portal so it escapes any overflow:hidden parents
 * and sits on top of drawers and modals with its own z-index. Closes
 * on outside click or Escape.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   const anchorRef = useRef<HTMLButtonElement>(null);
 *   <button ref={anchorRef} onClick={() => setOpen(true)}>Nov 1, 2026</button>
 *   <NovalaDatePicker
 *     isOpen={open}
 *     anchorEl={anchorRef.current}
 *     value="2026-11-01"
 *     onChange={(iso) => { setValue(iso); setOpen(false); }}
 *     onClose={() => setOpen(false)}
 *   />
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

/** Full month names for the month chip menu. */
const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** Two-letter weekday headers; Sunday first. */
const DOW_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

export interface NovalaDatePickerProps {
  isOpen: boolean;
  /** ISO date YYYY-MM-DD, or null if nothing selected. */
  value: string | null;
  /** Called with ISO date when a day is picked. The picker does NOT auto-close; parent decides. */
  onChange: (isoDate: string) => void;
  /** Called when the picker should close (outside click, Esc, chip picks a date). */
  onClose: () => void;
  /** Element to position the popover under. Falls back to document center if null. */
  anchorEl: HTMLElement | null;
  /** Optional lower bound (inclusive). */
  minDate?: string;
  /** Optional upper bound (inclusive). */
  maxDate?: string;
  /** Year range for the year chip menu. Defaults to currentYear - 5 to currentYear + 10. */
  yearRange?: { from: number; to: number };
}

/** Parse YYYY-MM-DD into a local-midnight Date; null-safe. */
function parseISO(s: string | null): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Format a Date into YYYY-MM-DD using local time (not UTC). */
function toISO(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** True when two Date objects fall on the same calendar day (local). */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function NovalaDatePicker({
  isOpen,
  value,
  onChange,
  onClose,
  anchorEl,
  minDate,
  maxDate,
  yearRange,
}: NovalaDatePickerProps) {
  const today = useMemo(() => new Date(), []);
  const selected = useMemo(() => parseISO(value), [value]);

  // The month being viewed; starts at the selected month, or today's month.
  const [viewYear, setViewYear] = useState<number>(
    selected?.getFullYear() ?? today.getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState<number>(
    selected?.getMonth() ?? today.getMonth(),
  );
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);

  const popoverRef = useRef<HTMLDivElement>(null);

  // When the popover reopens, re-center the view on the selected date.
  useEffect(() => {
    if (isOpen) {
      const d = selected ?? today;
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      setMonthMenuOpen(false);
      setYearMenuOpen(false);
    }
  }, [isOpen, selected, today]);

  // Close on Esc or outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    // Add on next frame so the click that opened the picker doesn't close it.
    const t = window.setTimeout(() => {
      document.addEventListener("mousedown", onClick);
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [isOpen, onClose, anchorEl]);

  // Compute popover anchor position (below the trigger, left-aligned).
  const popoverStyle = useMemo<CSSProperties>(() => {
    const base: CSSProperties = {
      position: "fixed",
      zIndex: 2000,
    };
    if (!anchorEl) {
      return { ...base, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    const r = anchorEl.getBoundingClientRect();
    const width = 280;
    const top = r.bottom + 6;
    const left = Math.max(8, Math.min(window.innerWidth - width - 8, r.left));
    return { ...base, top, left, width };
  }, [anchorEl]);

  // ---------- Month navigation ----------
  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  // ---------- Day grid ----------
  const grid = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startDow = firstOfMonth.getDay(); // 0 = Sun
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

    const cells: Array<{ date: Date; inMonth: boolean }> = [];
    // Leading cells from prev month
    for (let i = startDow - 1; i >= 0; i--) {
      cells.push({
        date: new Date(viewYear, viewMonth - 1, daysInPrev - i),
        inMonth: false,
      });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(viewYear, viewMonth, d), inMonth: true });
    }
    // Trailing cells from next month, to complete the 6-row grid
    const trailing = 42 - cells.length;
    for (let d = 1; d <= trailing; d++) {
      cells.push({ date: new Date(viewYear, viewMonth + 1, d), inMonth: false });
    }
    return cells;
  }, [viewYear, viewMonth]);

  // ---------- Year menu range ----------
  const years = useMemo(() => {
    const from = yearRange?.from ?? today.getFullYear() - 5;
    const to = yearRange?.to ?? today.getFullYear() + 10;
    const out: number[] = [];
    for (let y = from; y <= to; y++) out.push(y);
    return out;
  }, [yearRange, today]);

  // ---------- Date bounds ----------
  const minD = useMemo(() => parseISO(minDate ?? null), [minDate]);
  const maxD = useMemo(() => parseISO(maxDate ?? null), [maxDate]);
  const isDisabled = useCallback(
    (d: Date) => {
      if (minD && d < minD) return true;
      if (maxD && d > maxD) return true;
      return false;
    },
    [minD, maxD],
  );

  const handlePick = useCallback(
    (d: Date) => {
      if (isDisabled(d)) return;
      onChange(toISO(d));
    },
    [onChange, isDisabled],
  );

  if (!isOpen) return null;

  const popover = (
    <div ref={popoverRef} style={popoverStyle}>
      <div style={cardStyle}>
        <div style={headRowStyle}>
          <button type="button" style={navBtnStyle} onClick={prevMonth} aria-label="Previous month">
            <ChevronLeft size={14} />
          </button>

          {/* Month chip */}
          <div style={{ position: "relative" }}>
            <button
              type="button"
              style={chipStyle}
              onClick={() => {
                setMonthMenuOpen((v) => !v);
                setYearMenuOpen(false);
              }}
            >
              {MONTH_FULL[viewMonth]}
              <ChevronDown size={11} style={{ marginLeft: 2 }} />
            </button>
            {monthMenuOpen && (
              <div style={menuStyle}>
                {MONTH_FULL.map((name, i) => (
                  <button
                    key={name}
                    type="button"
                    style={{
                      ...menuItemStyle,
                      ...(i === viewMonth ? menuItemSelectedStyle : {}),
                    }}
                    onClick={() => {
                      setViewMonth(i);
                      setMonthMenuOpen(false);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Year chip */}
          <div style={{ position: "relative" }}>
            <button
              type="button"
              style={chipStyle}
              onClick={() => {
                setYearMenuOpen((v) => !v);
                setMonthMenuOpen(false);
              }}
            >
              {viewYear}
              <ChevronDown size={11} style={{ marginLeft: 2 }} />
            </button>
            {yearMenuOpen && (
              <div style={{ ...menuStyle, maxHeight: 240 }}>
                {years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    style={{
                      ...menuItemStyle,
                      ...(y === viewYear ? menuItemSelectedStyle : {}),
                    }}
                    onClick={() => {
                      setViewYear(y);
                      setYearMenuOpen(false);
                    }}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            style={{ ...navBtnStyle, marginLeft: "auto" }}
            onClick={nextMonth}
            aria-label="Next month"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div style={dowRowStyle}>
          {DOW_HEADERS.map((d, i) => (
            <div key={`${d}-${i}`} style={dowCellStyle}>
              {d}
            </div>
          ))}
        </div>

        <div style={gridStyle}>
          {grid.map(({ date, inMonth }) => {
            const isToday = sameDay(date, today);
            const isSelected = selected ? sameDay(date, selected) : false;
            const disabled = isDisabled(date);
            return (
              <button
                key={date.toISOString()}
                type="button"
                disabled={disabled}
                onClick={() => handlePick(date)}
                style={{
                  ...dayCellStyle,
                  ...(inMonth ? {} : dayCellOtherMonthStyle),
                  ...(isToday && !isSelected ? dayCellTodayStyle : {}),
                  ...(isSelected ? dayCellSelectedStyle : {}),
                  ...(disabled ? dayCellDisabledStyle : {}),
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(popover, document.body);
}

// ---------- Styles ----------

const cardStyle: CSSProperties = {
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  borderRadius: 10,
  boxShadow: "0 12px 32px -4px rgba(14, 26, 26, 0.18)",
  padding: 12,
  width: 280,
};

const headRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 10,
};

const navBtnStyle: CSSProperties = {
  width: 26,
  height: 26,
  border: "none",
  background: "transparent",
  borderRadius: 5,
  cursor: "pointer",
  color: "#3A4A52",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const chipStyle: CSSProperties = {
  padding: "5px 10px",
  border: "1px solid #D8DEE6",
  borderRadius: 6,
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 12.5,
  color: "#0E1A1A",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "inherit",
};

const menuStyle: CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  marginTop: 4,
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  borderRadius: 8,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.14)",
  padding: 4,
  minWidth: 120,
  maxHeight: 260,
  overflowY: "auto",
  zIndex: 10,
};

const menuItemStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "6px 10px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  fontSize: 12.5,
  color: "#0E1A1A",
  cursor: "pointer",
  borderRadius: 4,
  fontFamily: "inherit",
};

const menuItemSelectedStyle: CSSProperties = {
  background: "#F3F5F5",
  fontWeight: 600,
};

const dowRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: 2,
  marginBottom: 2,
};

const dowCellStyle: CSSProperties = {
  fontSize: 9.5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#6B7684",
  fontWeight: 600,
  textAlign: "center",
  padding: "4px 0",
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: 2,
};

const dayCellStyle: CSSProperties = {
  width: "100%",
  aspectRatio: "1 / 1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  borderRadius: 6,
  cursor: "pointer",
  color: "#0E1A1A",
  border: "none",
  background: "transparent",
  fontFamily: "inherit",
};

const dayCellOtherMonthStyle: CSSProperties = {
  color: "#6B7684",
  opacity: 0.4,
};

const dayCellTodayStyle: CSSProperties = {
  background: "#E7EAF0",
  fontWeight: 600,
};

const dayCellSelectedStyle: CSSProperties = {
  background: "#263238",
  color: "#ffffff",
  fontWeight: 700,
};

const dayCellDisabledStyle: CSSProperties = {
  color: "#D8DEE6",
  cursor: "not-allowed",
};

export default NovalaDatePicker;